/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactNode} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useMedia} from '../hooks/useMedia';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {DLSTheme, IconSize} from '../types/types';
import {getClasses} from './editorStyles';

export interface NotificationViewDialogProps {
  children: ReactNode;
  dialogTitle?: string | Node;
  open: boolean;
  onCloseDialog?: (e: MouseEvent) => void;
  onCancelDialog?: (e: MouseEvent<HTMLButtonElement>) => void,
  onPrimaryButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void,
  primaryButtonLoading?: boolean,
  primaryButtonDisabled?: boolean,
  styleOverrides?: (theme: DLSTheme) => GenericObject,
  omitActions?: boolean,
  primaryButtonDisabledText?: string
}

const styles = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    maxWidth                       : '1067px',
    overflow                       : 'hidden',
    margin                         : 'auto',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  titleSection : {
    background : dlsColor('grey6')
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  dialogTitle : {
    backround : dlsColor('grey6')
  },
  formDialogContent : {
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    },
    padding : 0
  }
});

/**
 * Wrapper component for form Dialogs
 *
 * @param children             Component that is to be wrapped
 * @param dialogTitle          Dialog title
 * @param open                 should the dialog be open
 * @param onCloseDialog        callback for when the dialog requests to be closed
 * @param onCancelDialog       callback for when the user clicks the cancel button
 * @param onPrimaryButtonClick callback to execute on main CTA button click
 * @param primaryButtonLoading if true we show the button spinner
 * @param styleOverrides       function that returns styles to override the base
 *
 * ```typescript
 *
 * import {NotificationViewDialog} from '@ion/dls/src/editors/';
 *
 * <NotificationViewDialog open={open}
 *   dialogTitle='Add Asset'
 *   onCloseDialog={onCloseDialog}
 *   onCancelDialog={onCancelDialog}
 *   onPrimaryButtonClick={onPrimaryButtonClick}
 *   primaryButtonLoading={true}>
 *   <Component />
 * </NotificationViewDialog>
 *
 * ```
 *
 */
export const NotificationViewDialog = ({
  children,
  onCloseDialog,
  styleOverrides,
  open
}: NotificationViewDialogProps) => {
  const dialogStyles           = useExtendStyles(styles, styleOverrides);
  const classes                = dialogStyles();
  const {isPhone}              = useMedia();
  return (
    <Dialog open={open}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCloseDialog} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
        </div>

      </DialogTitle>
      <DialogContent className={classes.formDialogContent}>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationViewDialog;
